import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface DashboardHeaderProps {
  title?: string;
  subtitle?: string;
  className?: string;
}

export function DashboardHeader({ 
  title = "Dashboard", 
  subtitle,
  className = "" 
}: DashboardHeaderProps) {
  const { userData } = useAuth();

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getDisplayName = () => {
    if (userData?.firstName && userData?.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    return userData?.email?.split('@')[0] || 'User';
  };

  const getUserRole = () => {    
    // Auto-detect role based on userData if no subtitle provided
    switch (userData?.role) {
      case 'recruiter':
        return 'Recruiter';
      case 'super_admin':
        return 'Super Admin';
      default:
        return 'Referrer';
    }
  };

  if (!userData) return null;

  return (
    <div className={`hidden sm:flex justify-between items-center mb-6 sm:mb-8 ${className}`}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{title}</h1>
        {subtitle && (
          <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
        )}
      </div>
      
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 sm:h-12 sm:w-12 ring-2 ring-gray-200">
          <AvatarImage src={userData.profileImageUrl || ""} />
          <AvatarFallback className="bg-gradient-to-br from-orange-400 to-orange-600 text-white font-bold text-sm sm:text-base">
            {getInitials(userData.firstName, userData.lastName)}
          </AvatarFallback>
        </Avatar>
        <div className="hidden md:block">
          <p className="text-base sm:text-lg font-semibold text-gray-900">
            {getDisplayName()}
          </p>
          <p className="text-xs sm:text-sm text-gray-500">
            {getUserRole()}
          </p>
        </div>
      </div>
    </div>
  );
}